import axios from 'axios';
import { redirectToLoginForAuthFailure } from '@/data/utils/authCleanup';

const cache = {
	data: null,
	timestamp: null
};

const CACHE_DURATION = 0.1 * 100;

const getUserTypeFromSubscription = subscriptionName => {
	if (!subscriptionName) return '';

	const subscriptionToUserType = {
		vip: 'vip',
		'studio-5': 'studio-owner',
		'studio-10': 'studio-owner',
		'studio-account-5': 'studio-owner',
		'studio-account-10': 'studio-owner',
		standard: ''
	};

	const mappedValue = subscriptionToUserType[subscriptionName];
	return mappedValue !== undefined ? mappedValue : '';
};

// A sub-user's subscription data is their studio owner's, so it must never change the sub-user's own userType.
const resolveUserType = (prevUser, mappedUserType, isActive = true) => {
	if (prevUser?.userType === 'subuser') return 'subuser';
	if (!isActive) return '';
	return mappedUserType || prevUser?.userType || '';
};

const checkApiStatus = async (token = '', setUserData = null) => {
	axios.defaults.withCredentials = true;

	const handleUnauthorized = () => {
		if (setUserData != null) {
			setUserData(prevUserData => ({
				...prevUserData,
				token: null,
				user: null,
				role: null,
				subscriptionId: ''
			}));
		}
		redirectToLoginForAuthFailure();
	};

	if (!token || token === 'null' || token === 'undefined') {
		return false;
	}

	// Check if cached data is still valid (within 30 minutes)
	const currentTime = new Date().getTime();
	if (cache.data && cache.timestamp && currentTime - cache.timestamp < CACHE_DURATION) {
		console.log('Using cached API data');
		const response = cache.data;

		// Convert subscription_name to userType
		const userType = getUserTypeFromSubscription(response?.data?.subData?.subscription_name);

		if (setUserData != null) {
			setUserData(prevUserData => ({
				...prevUserData,
				user: {
					...prevUserData.user,
					isTrialAvailed: response?.data?.isTrialAvailed ?? false,
					userType: resolveUserType(prevUserData.user, userType)
				}
			}));
		}

		return handleApiResponse(response, setUserData);
	}

	try {
		const response = await axios.get(`${import.meta.env.VITE_APP_API_URL}/fetch/subscribtion`, {
			headers: { Authorization: `Bearer ${token}` }
		});

		// Check for 401 status in response
		if (response.status === 401) {
			handleUnauthorized();
			return false;
		}

		// Update the cache
		cache.data = response;
		cache.timestamp = currentTime;

		// Convert subscription_name to userType
		const userType = getUserTypeFromSubscription(response?.data?.subData?.subscription_name);

		if (setUserData != null) {
			setUserData(prevUserData => ({
				...prevUserData,
				user: {
					...prevUserData.user,
					isTrialAvailed: response?.data?.isTrialAvailed ?? false,
					userType: resolveUserType(prevUserData.user, userType)
				}
			}));
		}

		return handleApiResponse(response, setUserData);
	} catch (error) {
		console.error('Error calling API:', error);

		// Check for 401 status in error response
		if (error.response?.status === 401 || error.status === 401) {
			handleUnauthorized();
			return false;
		}
		return false;
	}
};

const handleApiResponse = (response, setUserData = () => {}) => {
	if (response.status === 401) {
		redirectToLoginForAuthFailure();
		return false;
	}

	if (response.status === 200) {
		const { data } = response;
		const { subData, data: subscriptionStatus } = data;

		localStorage.setItem('subscriptionId', subData?.SubscribtionId ?? '');

		// Convert subscription_name to userType
		const userType = getUserTypeFromSubscription(subData?.subscription_name);

		if (subscriptionStatus === 'in_trial' || subscriptionStatus === 'active' || subscriptionStatus === 'non_renewing') {
			if (setUserData != null) {
				setUserData(prevUserData => ({
					...prevUserData,
					subscriptionId: subData?.SubscribtionId ?? '',
					user: {
						...prevUserData.user,
						subscription: true,
						userType: resolveUserType(prevUserData.user, userType),
						isTrialAvailed: true
					}
				}));
			}
			return true;
		} else {
			localStorage.setItem('subscriptionId', '');
			if (setUserData != null) {
				setUserData(prevUserData => ({
					...prevUserData,
					subscriptionId: '',
					user: {
						...prevUserData.user,
						userType: resolveUserType(prevUserData.user, userType, false),
						subscription: false,
						isTrialAvailed: response?.data?.isTrialAvailed ?? prevUserData.user?.isTrialAvailed ?? false
					}
				}));
			}
			return false;
		}
	}
	return false;
};

export default checkApiStatus;
